import { useMemo } from "react";
import { useRecords } from "../../../api/useRecords";
import { CalendarFiltersType } from "../calendar.types";
import { OverlayEvent } from "./overlay.types";
import { createOverlayScheduleFilter, createOverlayTimeOffFilter } from "./overlayFilters";
import { SERVICE_CATEGORY_ID_MAPPING } from "../serviceCategoryMapping";

const matchesServiceFilter = (event: OverlayEvent, selectedCategories: Array<string>) => {
    if (selectedCategories.length === 0) return true;

    const categoryFilters = selectedCategories.filter((value) => !SERVICE_CATEGORY_ID_MAPPING[value]);
    const serviceIdFilters = selectedCategories
        .filter((value) => SERVICE_CATEGORY_ID_MAPPING[value])
        .map((value) => SERVICE_CATEGORY_ID_MAPPING[value]);

    const hasMatchingCategory = (event.serviceCategories || []).some((category) =>
        categoryFilters.includes(category)
    );
    const hasMatchingServiceId = (event.serviceIds || []).some((id) => serviceIdFilters.includes(id));

    const matches = hasMatchingCategory || hasMatchingServiceId;
    return matches;
};

const SCHEDULE_FIELDS = "id,start_date,end_date,clinic_id,user.id,user.first_name,user.last_name,user.technician_id.id,user.technician_id.color,user.technician_id.disabled,services.services_id.id,services.services_id.category";

const TIME_OFF_FIELDS = "id,start_date,end_date,type,clinic_id,user.id,user.first_name,user.last_name";

const TECHNICIAN_FIELDS = "id,user_id,disabled,specializations,color";

const ONE_HOUR_MS = 60 * 60 * 1000;

type UseScheduleOverlayArgs = {
    filters: CalendarFiltersType | null;
    enabled: boolean;
    showTimeOff: boolean;
    showBackoffice: boolean;
};

const extractFirstName = (firstName: string | undefined) => {
    const safeName = firstName || "";
    return safeName;
};

const projectScheduleToOverlay = (schedule: any): OverlayEvent => {
    const firstName = extractFirstName(schedule.user?.first_name);
    const lastName = schedule.user?.last_name || "";
    const serviceEntries = Array.isArray(schedule.services) ? schedule.services : [];
    const serviceIds = serviceEntries
        .map((entry: any) => entry?.services_id?.id)
        .filter((id: any): id is number => typeof id === "number");
    const isBackoffice = serviceIds.length === 0;
    const activeTechnician = schedule.user?.technician_id?.find((tech: any) => tech?.disabled !== true);
    const overlayEvent = {
        id: schedule.id,
        start: new Date(schedule.start_date),
        end: new Date(schedule.end_date),
        allDay: schedule.start_date === schedule.end_date,
        overlayType: "schedule" as const,
        userId: schedule.user?.id,
        userName: `${firstName} ${lastName}`.trim(),
        userColor: activeTechnician?.color,
        serviceIds,
        serviceCategories: serviceEntries
            .map((entry: any) => entry?.services_id?.category)
            .filter((category: any): category is string => typeof category === "string"),
        isBackoffice,
        title: firstName,
    };
    return overlayEvent;
};

const projectTimeOffToOverlay = (timeOff: any): OverlayEvent => {
    const firstName = extractFirstName(timeOff.user?.first_name);
    const lastName = timeOff.user?.last_name || "";
    const title = timeOff.type ? `${firstName} · ${timeOff.type}` : firstName;
    const overlayEvent = {
        id: timeOff.id,
        start: new Date(timeOff.start_date),
        end: new Date(timeOff.end_date),
        allDay: true,
        overlayType: "time_off" as const,
        userId: timeOff.user?.id,
        userName: `${firstName} ${lastName}`.trim(),
        userColor: "#e63131",
        timeOffType: timeOff.type,
        title,
    };
    return overlayEvent;
};

export const useScheduleOverlay = ({ filters, enabled, showTimeOff, showBackoffice }: UseScheduleOverlayArgs) => {
    const scheduleFilter = useMemo(() => createOverlayScheduleFilter(filters), [filters]);
    const timeOffFilter = useMemo(() => createOverlayTimeOffFilter(filters), [filters]);

    const { data: techniciansData } = useRecords("technicians", TECHNICIAN_FIELDS, {
        extraOptions: { pageSize: -1 },
        queryKeyExtraValue: "schedule-overlay",
        enabled,
        staleTime: ONE_HOUR_MS,
    });

    const activeTechnicianUserIds = useMemo(() => {
        const items = techniciansData?.items || [];
        const ids = items
            .filter((tech: any) => {
                const notDisabled = tech?.disabled !== true;
                const hasSpecializations = Array.isArray(tech?.specializations) && tech.specializations.length > 0;
                return notDisabled && hasSpecializations;
            })
            .map((tech: any) => tech?.user_id)
            .filter((userId: any): userId is string => typeof userId === "string");
        const set = new Set<string>(ids);
        return set;
    }, [techniciansData]);

    const { data: schedulesData, status: schedulesStatus } = useRecords("schedules", SCHEDULE_FIELDS, {
        extraOptions: { filter: scheduleFilter, pageSize: -1 },
        enabled: enabled && !!scheduleFilter,
    });

    const { data: timeOffData, status: timeOffStatus } = useRecords("time_off", TIME_OFF_FIELDS, {
        extraOptions: { filter: timeOffFilter, pageSize: -1 },
        enabled: enabled && showTimeOff && !!timeOffFilter,
    });

    const overlayEvents = useMemo(() => {
        const selectedCategories = filters?.service_category || [];

        const technicianSchedules = (schedulesData?.items || []).filter((schedule: any) => {
            const userId = schedule?.user?.id;
            const isActiveTechnician = typeof userId === "string" && activeTechnicianUserIds.has(userId);
            return isActiveTechnician;
        });
        const allScheduleEvents: Array<OverlayEvent> = technicianSchedules.map(projectScheduleToOverlay);
        const scheduleEventsAfterService = allScheduleEvents.filter((event: OverlayEvent) => {
            if (event.isBackoffice) return showBackoffice;
            const serviceMatches = matchesServiceFilter(event, selectedCategories);
            return serviceMatches;
        });

        const technicianTimeOff = showTimeOff
            ? (timeOffData?.items || []).filter((item: any) => {
                  const userId = item?.user?.id;
                  const isActiveTechnician = typeof userId === "string" && activeTechnicianUserIds.has(userId);
                  return isActiveTechnician;
              })
            : [];
        const timeOffOverlayEvents = technicianTimeOff.map(projectTimeOffToOverlay);

        const combined = [...scheduleEventsAfterService, ...timeOffOverlayEvents];
        return combined;
    }, [schedulesData, timeOffData, showTimeOff, showBackoffice, filters?.service_category, activeTechnicianUserIds]);

    const isLoading = schedulesStatus === "loading" || timeOffStatus === "loading";

    return { overlayEvents, isLoading };
};
